/**
 * Staff-login options for a coach record (DNKADM-0362; dink-api handoff §5,
 * DNKAPI-0520).
 *
 * A `v2_staff` (bookable coach) row carries `authUserId` — the `userId` of a **Team &
 * access roster row with `kind: 'STAFF'`**. This module turns the roster into the
 * pickable set and resolves a stored `authUserId` back to human copy, so the number
 * itself is never rendered anywhere.
 *
 * Two rules the roster forces (handoff §4):
 *  - only staff rows are offerable — a CUSTOMER row is rejected by the server with
 *    `400 STAFF_LINK_NOT_IN_ROSTER`, so the picker must never offer one. `kind` is the
 *    answer when the row carries it; `isStaffRosterRow` keeps the pre-flip role-set
 *    fallback for a row that predates DNKAPI-0517;
 *  - a dual-role person is TWO rows with the SAME `userId` (one STAFF, one CUSTOMER).
 *    Filtering to staff already collapses that to one row; the id-dedupe below is the
 *    belt to that braces, so the person can never appear twice in one select.
 */
import type { PlatformMember } from '@/api/platformMembers';
import { isStaffRosterRow } from '@/domain/rosterKind';

/** One offerable staff login: the `authUserId` to send, and the copy to show. */
export interface StaffLoginOption {
  userId: number;
  /** `name (email)` — see {@link staffLoginLabel}. Never a bare id. */
  label: string;
}

/**
 * Human copy for one roster row: `name (email)`, degrading to whichever half the row
 * actually carries and finally to a neutral placeholder. NEVER the `userId` — a raw
 * platform id is not something an operator can act on.
 */
function staffLoginLabel(member: Pick<PlatformMember, 'name' | 'email'>): string {
  const name = member.name.trim();
  const email = member.email.trim();
  if (name !== '' && email !== '') return `${name} (${email})`;
  if (name !== '') return name;
  if (email !== '') return email;
  return 'Unnamed login';
}

/**
 * The staff logins a coach record may link to, in roster order (staff rows come first
 * on the wire), each person once.
 */
export function toStaffLoginOptions(members: PlatformMember[]): StaffLoginOption[] {
  const seen = new Set<number>();
  const options: StaffLoginOption[] = [];
  for (const member of members) {
    if (!isStaffRosterRow(member)) continue;
    if (seen.has(member.userId)) continue;
    seen.add(member.userId);
    options.push({ userId: member.userId, label: staffLoginLabel(member) });
  }
  return options;
}

/**
 * The label for a coach's linked login, or `null` when it cannot be resolved — no
 * link at all, the roster not loaded/failed, or a login that has left the roster.
 * Callers render their own copy for `null`; nobody renders the id.
 */
export function resolveStaffLoginLabel(
  options: StaffLoginOption[],
  authUserId: number | null,
): string | null {
  if (authUserId === null) return null;
  return options.find((option) => option.userId === authUserId)?.label ?? null;
}
